/**
 * Сортировка категорий турнира: по возрасту, затем по весу (по возрастанию — легкие сначала), затем девочки перед мальчиками.
 * Используется везде при отображении списка категорий турнира.
 */
export interface WeightCategoryLike {
  id?: number;
  name?: string;
  ageFrom?: number;
  ageTo?: number;
  weightFrom?: number;
  weightTo?: number;
  gender?: string;
}

/** Минимальный интерфейс для сортировки; совместим с TournamentCategory, Category, CategoryResults и т.д. */
export interface CategoryLike {
  id?: number;
  name?: string;
  minAge?: number;
  ageFrom?: number;
  maxAge?: number;
  ageTo?: number;
  minWeight?: number;
  weightFrom?: number;
  weightTo?: number;
  gender?: string;
  weightCategory?: WeightCategoryLike;
}

const genderOrder: Record<string, number> = { female: 1, male: 2, mixed: 3 };

/** Извлекает число (возраст или вес) из названия категории для сортировки, если в полях не задано. */
function parseNumberFromName(name: string | undefined, pattern: RegExp): number | null {
  if (!name || typeof name !== 'string') return null;
  const m = name.match(pattern);
  if (!m) return null;
  const num = parseInt(m[1] || m[0], 10);
  return Number.isNaN(num) ? null : num;
}

function getCategoryAge(c: CategoryLike): number {
  const fromField = c.minAge ?? c.ageFrom ?? c.weightCategory?.ageFrom;
  if (typeof fromField === 'number' && !Number.isNaN(fromField)) return fromField;
  const name = (c.name as string) ?? '';
  const age = parseNumberFromName(name, /(\d+)\s*[-–]\s*(\d+)\s*лет/) ?? parseNumberFromName(name, /(\d+)\s*лет/);
  return age ?? 0;
}

function getCategoryWeightFrom(c: CategoryLike): number {
  const fromField = c.weightFrom ?? c.minWeight ?? c.weightCategory?.weightFrom;
  const name = (c.name as string) ?? (c.weightCategory as { name?: string })?.name ?? '';
  const fromName = parseNumberFromName(name, /весовая категория\s*(\d+)/i)
    ?? parseNumberFromName(name, /(\d+)\s*кг/)
    ?? parseNumberFromName(name, /[-–]?\s*(\d+)\s*кг/);
  if (typeof fromField === 'number' && !Number.isNaN(fromField)) return fromField;
  return fromName ?? 0;
}

function getCategoryGender(c: CategoryLike): string {
  return (c.gender ?? c.weightCategory?.gender ?? 'mixed').toLowerCase();
}

/** Сравнение двух категорий для сортировки: возраст → вес (по возрастанию) → девочки перед мальчиками. Возвращает -1, 0 или 1. */
export function compareTournamentCategories(a: CategoryLike, b: CategoryLike): number {
  const ageA = getCategoryAge(a);
  const ageB = getCategoryAge(b);
  if (ageA !== ageB) return ageA - ageB;

  const weightA = getCategoryWeightFrom(a);
  const weightB = getCategoryWeightFrom(b);
  if (weightA !== weightB) return weightA - weightB;

  const genderA = genderOrder[getCategoryGender(a)] ?? 3;
  const genderB = genderOrder[getCategoryGender(b)] ?? 3;
  return genderA - genderB;
}

export function sortTournamentCategories<T extends CategoryLike>(categories: T[]): T[] {
  return [...categories].sort((a, b) => compareTournamentCategories(a, b));
}
